import { useEffect, useState, useMemo } from "react";
import { Button, Popover, Portal, HStack, Heading, createListCollection, Select, Text, Wrap, WrapItem, VStack, Dialog, CloseButton, Table, Separator, Box } from "@chakra-ui/react";
import { LuLayers, LuKey, LuFocus, LuTable, LuSearchCheck, LuWrench } from "react-icons/lu";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";

import { matchKeyInText, removeFileExtension, showToast } from "../../../utilies/Utility";
import { blinkEntity, getPropertyKeysFromDataSource } from "../../../utilies/EntityUtility";
import { getEntitiesWithInvalidLineDistances } from "../../../utilies/validation/lanelet/Distance";
import { getEntitiesWithInvalidLineTypes } from "../../../utilies/validation/lanelet/Type";
import { getEntitiesMissingAnyKeys } from "../../../utilies/validation/MissingKey";
import { getEntitiesWithEmptyValuesForKeys } from "../../../utilies/validation/EmptyValue";
import { getEntitiesWithInvalidPropertyEnums } from "../../../utilies/validation/InvalidValue";
import Type from "../../../utilies/Types";
import { getEntitiesWithDuplicateValuesForKeys } from "../../../utilies/validation/Duplicate";
import { useMapObjects } from "../../../context/MapObjectsContext";
import { getEntitiesWithOverlappingPolylines } from "../../../utilies/validation/lanelet/Overlapping";
import { getEntitiesWithNegativeHeights } from "../../../utilies/validation/NegativeAlt";
import { getComprehensiveLaneletIssues } from "../../../utilies/validation/lanelet/ComprehensiveLanelet";
import { getLaneletsWithMissingSideIds } from "../../../utilies/validation/lanelet/MissingSides";
import { getLaneletsWithOpenSides } from "../../../utilies/validation/lanelet/ConnectionSides";
import { fixInvalidLineTypes } from "../../../utilies/validationAutoFix/lanelet/Type";
import { fixMissingSideIds } from "../../../utilies/validationAutoFix/lanelet/MissingSides";
import { markEntityLayerEdited } from "../../../utilies/entity/UpdateMapObjectEdited";

const ResultsTable = ({ dialogState, data = [], selectedDataSource, activeRule }) => {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const [rows, setRows] = useState(() => (Array.isArray(data) ? data : []));
  const { setMapObjects } = useMapObjects();

  useEffect(() => {
    setRows(Array.isArray(data) ? data : []);
  }, [data]);

  const PAGE_SIZE = 100;

  const [selectedReason, setSelectedReason] = useState("all");
  const [page, setPage] = useState(1);

  const filteredData = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    if (selectedReason === "all") return rows;

    return rows.filter((item) => {
      if (!item) return false;
      if (Array.isArray(item.reasons)) return item.reasons.includes(selectedReason);
      return item.reasons === selectedReason;
    });
  }, [rows, selectedReason]);

  useEffect(() => {
    setPage(1);
  }, [selectedReason, data]);

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const start = (page - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;

  const pagedData = useMemo(() => filteredData.slice(start, end), [filteredData, start, end]);

  const fixableLineTypeItems = useMemo(() => {
    return filteredData.filter((x) => x?.type === "lanelet_type");
  }, [filteredData]);

  const handleFixAllLineTypes = () => {
    if (!viewerRef?.current) return;
    if (!selectedDataSource) return;

    if (fixableLineTypeItems.length === 0) return;

    const report = fixInvalidLineTypes(viewerRef.current, selectedDataSource, fixableLineTypeItems) || [];

    const idsToRemove = new Set();

    for (let i = 0; i < fixableLineTypeItems.length; i++) {
      const item = fixableLineTypeItems[i];
      const r = report[i] || {};
      const sides = r?.sides || [];

      const reasons = Array.isArray(item.reasons) ? item.reasons : [item.reasons];
      const needLeft = reasons.includes("Left line type is invalid");
      const needRight = reasons.includes("Right line type is invalid");

      const fixedLeft = !needLeft || sides.includes("left");
      const fixedRight = !needRight || sides.includes("right");

      if (fixedLeft && fixedRight) {
        if (item?.entity) {
          markEntityLayerEdited(item.entity, setMapObjects, viewerRef.current);
        }
        idsToRemove.add(item.id);
      }
    }

    setRows((prev) => prev.filter((x) => !idsToRemove.has(x?.id)));
  };

  const handleFixAllOverlapping = () => {
    if (!viewerRef?.current) return;
    if (!selectedDataSource) return;

    const list = Array.isArray(filteredData) ? filteredData : [];
    if (list.length === 0) {
      setRows([]);

      return;
    }

    let removed = 0;

    for (let i = 0; i < list.length; i++) {
      const item = list[i];
      const ent = item?.removeEntity || item?.entity;
      if (!ent) continue;

      const entId = ent?.id ?? item?.id;

      try {
        if (selectedDataSource?.entities?.remove && ent) {
          if (selectedDataSource.entities.remove(ent)) removed++;
        } else if (selectedDataSource?.entities?.removeById && entId != null) {
          if (selectedDataSource.entities.removeById(entId)) removed++;
        } else if (selectedDataSource?.remove && ent) {
          if (selectedDataSource.remove(ent)) removed++;
        } else if (selectedDataSource?.removeById && entId != null) {
          if (selectedDataSource.removeById(entId)) removed++;
        }
      } catch (e) {}

      try {
        markEntityLayerEdited(ent, setMapObjects, viewerRef.current);
      } catch (e) {}
    }

    setRows([]);

    if (removed === 0) {
      showToast("Nothing removed", Type.NOTIFICATION.WARNING, { closable: true });
    } else {
      showToast(`Removed ${removed} entities`, Type.NOTIFICATION.SUCCESS, { closable: true });
    }
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
    if (page > newTotalPages) setPage(newTotalPages);
  }, [filteredData.length, page]);

  return (
    <Dialog.Root scrollBehavior="inside" placement="top" motionPreset="slide-in-right" closeOnInteractOutside={false} modal={false} open={dialogState[0]} onOpenChange={(e) => dialogState[1](e.open)}>
      <Portal>
        <Dialog.Positioner pointerEvents="none" zIndex={1002}>
          <Dialog.Content mr="70px" mt="100px" width="fit-content" pointerEvents="all">
            <Dialog.Header>
              <VStack gap={0} width="full">
                <Dialog.Title textAlign="left" width="full">
                  Results ({totalItems})
                </Dialog.Title>
              </VStack>
            </Dialog.Header>

            <Dialog.Body>
              <HStack mb={3} justifyContent="space-between">
                <Text fontSize="sm">Fixable (Line Types): {fixableLineTypeItems.length}</Text>

                <HStack>
                  {activeRule === "overlapping" && filteredData.length > 0 && (
                    <Button size="sm" height="30px" onClick={handleFixAllOverlapping}>
                      Auto Fix <LuWrench />
                    </Button>
                  )}

                  {fixableLineTypeItems.length > 0 && selectedDataSource && viewerRef?.current && (
                    <Button size="sm" height="30px" onClick={handleFixAllLineTypes}>
                      Fix all line types <LuWrench />
                    </Button>
                  )}
                </HStack>
              </HStack>

              <Table.Root size="sm" striped>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>ID</Table.ColumnHeader>
                    <Table.ColumnHeader>Reason</Table.ColumnHeader>
                    <Table.ColumnHeader>Actions</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {pagedData.map((item) => (
                    <Table.Row key={item.id}>
                      <Table.Cell>{item.id}</Table.Cell>

                      <Table.Cell>{Array.isArray(item.reasons) ? item.reasons.map((r, i) => <div key={i}>{r}</div>) : item.reasons}</Table.Cell>

                      <Table.Cell>
                        <Button
                          bg="transparent"
                          outline="none"
                          height="30px"
                          px={0}
                          onClick={() => {
                            blinkEntity(viewerRef.current, item.entity, {
                              zoom: true,
                              duration: 2000,
                              step: 200,
                            });
                          }}
                        >
                          <LuFocus color="MenuText" />
                        </Button>
                        {item?.type && (
                          <Button
                            px={0}
                            bg="transparent"
                            outline="none"
                            height="30px"
                            onClick={() => {
                              switch (item.type) {
                                case "lanelet_type": {
                                  const [r] = fixInvalidLineTypes(viewerRef.current, selectedDataSource, [item]);

                                  const reasons = Array.isArray(item.reasons) ? item.reasons : [item.reasons];
                                  const needLeft = reasons.includes("Left line type is invalid");
                                  const needRight = reasons.includes("Right line type is invalid");

                                  const fixedLeft = !needLeft || (r?.sides || []).includes("left");
                                  const fixedRight = !needRight || (r?.sides || []).includes("right");

                                  if (fixedLeft && fixedRight) {
                                    // Mark layer as edited
                                    if (item?.entity) {
                                      markEntityLayerEdited(item.entity, setMapObjects, viewerRef.current);
                                    }

                                    // Remove fixed row
                                    setRows((prev) => prev.filter((x) => x?.id !== item.id));
                                  }
                                  break;
                                }

                                case "lanelet_missing_sides": {
                                  const [r] = fixMissingSideIds(viewerRef.current, selectedDataSource, [item]) || [{}];

                                  const reasons = Array.isArray(item.reasons) ? item.reasons : [item.reasons];
                                  const needLeft = reasons.includes("Left ID not found");
                                  const needRight = reasons.includes("Right ID not found");

                                  const fixedLeft = !needLeft || (r?.sides || []).includes("left");
                                  const fixedRight = !needRight || (r?.sides || []).includes("right");

                                  if (fixedLeft && fixedRight) {
                                    // Mark layer as edited
                                    if (item?.entity) {
                                      markEntityLayerEdited(item.entity, setMapObjects, viewerRef.current);
                                    }

                                    // Remove fixed row
                                    setRows((prev) => prev.filter((x) => x?.id !== item.id));
                                  }

                                  break;
                                }
                              }
                            }}
                          >
                            <LuWrench color="MenuText" />
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}

                  {pagedData.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={3}>No results</Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Root>

              <Box style={{ display: "flex", alignItems: "center", gap: 8 }} mt={5}>
                <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={!canPrev}>
                  Prev
                </Button>

                <span style={{ whiteSpace: "nowrap" }}>
                  Page {page} / {totalPages} — Showing {totalItems === 0 ? 0 : Math.min(end, totalItems)} of {totalItems}
                </span>

                <Button size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={!canNext}>
                  Next
                </Button>
              </Box>
            </Dialog.Body>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

function ValidationTool() {
  const [resDialog, setResDialog] = useState(false);
  const [validationDialog, setValidationDialog] = useState(false);
  const [layerSelectValue, setLayerSelectValue] = useState([]);

  const viewerRef = useAppStore((s) => s.viewerRef);
  const { mapObjects } = useMapObjects();

  const [selectedLayers, setSelectedLayers] = useState([]);
  const [layers, setLayers] = useState(
    createListCollection({
      items: [],
    }),
  );
  const [validationResults, setValidationResults] = useState([]);
  const [activeRule, setActiveRule] = useState("");

  const [propertiesKeys, setPropertiesKeys] = useState(
    createListCollection({
      items: [],
    }),
  );

  const [selectedPropertiesKeys, setSelectedPropertiesKeys] = useState([]);

  useEffect(() => {
    let tempLayers = [{ label: "All Layers", value: "all-layers" }];
    for (const key in mapObjects) {
      let fileName = removeFileExtension(mapObjects[key].file_name);
      tempLayers.push({ label: fileName, value: mapObjects[key].id });
    }
    if (tempLayers.length > 1)
      setLayers(
        createListCollection({
          items: tempLayers,
        }),
      );
    else {
      reset();
    }
  }, [mapObjects]);

  useEffect(() => {
    if (selectedLayers.length != 0) {
      if (selectedLayers.length == 1) {
        const keys = getPropertyKeysFromDataSource(selectedLayers[0].dataSource);
        let tempKeys = [];
        for (const key in keys) {
          tempKeys.push({ label: keys[key], value: keys[key] });
        }
        setPropertiesKeys(
          createListCollection({
            items: tempKeys,
          }),
        );
      } else {
      }
    }
  }, [selectedLayers]);

  const reset = () => {
    setSelectedLayers([]);
    setValidationResults([]);
    setPropertiesKeys(
      createListCollection({
        items: [],
      }),
    );
    setSelectedPropertiesKeys([]);
    setResDialog(false);
    setLayerSelectValue([]);
    setActiveRule("");
  };

  return (
    <>
      <Popover.Root
        positioning={{ placement: "left-start" }}
        open={validationDialog}
        onOpenChange={(e) => {
          setValidationDialog(e.open);
        }}
      >
        <Popover.Trigger asChild>
          <Button userSelect="none" p={0} bg={"Background"}>
            <Tooltip content="Validation">
              <LuSearchCheck color="MenuText" />
            </Tooltip>
          </Button>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
            <Popover.Content maxWidth={"420px"} width={"fit-content"} minWidth={"300px"}>
              <Popover.Arrow />
              <Popover.Body px={3} py={1} maxWidth={"420px"} width={"fit-content"} minWidth={"300px"}>
                <Heading fontSize={"md"}>Validation</Heading>

                <Select.Root
                  disabled={mapObjects.length == 0}
                  collection={layers}
                  size="sm"
                  width="full"
                  mt={3}
                  mb={3}
                  value={layerSelectValue}
                  onValueChange={(val) => {
                    setLayerSelectValue(val.value);

                    let selected = [];
                    for (const key in mapObjects) {
                      if (val.value[0] !== "all-layers" && mapObjects[key].id == val.value[0]) {
                        selected.push(mapObjects[key]);
                        break;
                      } else if (val.value[0] === "all-layers") {
                        selected.push(mapObjects[key]);
                      }
                    }

                    setSelectedLayers(selected);
                    setSelectedPropertiesKeys([]);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Label>
                    <HStack>
                      <LuLayers /> Select Layer
                    </HStack>
                  </Select.Label>
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder="Select Layer" />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.ClearTrigger />
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>

                  <Select.Positioner>
                    <Select.Content>
                      {layers.items.map((layer) => (
                        <Select.Item item={layer} key={layer.value}>
                          {layer.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>

                {selectedLayers.length == 1 && (
                  <Select.Root
                    multiple
                    collection={propertiesKeys}
                    size="sm"
                    width="full"
                    mt={3}
                    mb={3}
                    onValueChange={(val) => {
                      setSelectedPropertiesKeys(val.value);
                    }}
                  >
                    <Select.HiddenSelect />
                    <Select.Label>
                      <HStack>
                        <LuKey /> Select Key(s)
                      </HStack>
                    </Select.Label>
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="Select Key(s)" />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {propertiesKeys.items.map((pKey) => (
                          <Select.Item item={pKey} key={pKey.value}>
                            {pKey.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}

                {selectedLayers.length != 0 && (
                  <>
                    <Text>Public Methods: </Text>
                    <Wrap spacing="4px" mb={2}>
                      <WrapItem>
                        <Button colorPalette={"red"} height="30px" onClick={reset}>
                          Reset
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          disabled={selectedPropertiesKeys.length == 0}
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesMissingAnyKeys(selectedLayers[0].dataSource, selectedPropertiesKeys);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Missing Key(s)
                        </Button>
                      </WrapItem>

                      <WrapItem>
                        <Button
                          disabled={selectedPropertiesKeys.length == 0}
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesWithEmptyValuesForKeys(selectedLayers[0].dataSource, selectedPropertiesKeys);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Empty Value
                        </Button>
                      </WrapItem>

                      <WrapItem>
                        <Button
                          disabled={selectedPropertiesKeys.length == 0}
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesWithInvalidPropertyEnums(selectedLayers[0].dataSource);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Invalid Value
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          disabled={selectedPropertiesKeys.length == 0}
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesWithDuplicateValuesForKeys(selectedLayers[0].dataSource, selectedPropertiesKeys);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Check Duplicates
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesWithNegativeHeights(selectedLayers[0].dataSource, selectedPropertiesKeys);

                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Finding Negative Altitude
                        </Button>
                      </WrapItem>
                      {selectedLayers.length == 1 &&
                        (matchKeyInText(selectedLayers[0].dataSource.layerName, "lanelet") ||
                          matchKeyInText(selectedLayers[0].dataSource.layerName, "lane_marker") ||
                          matchKeyInText(selectedLayers[0].dataSource.layerName, "lanemarker")) && (
                          <WrapItem>
                            <Button
                              height="30px"
                              onClick={() => {
                                let res = getEntitiesWithOverlappingPolylines(viewerRef.current, selectedLayers[0].dataSource);
                                setActiveRule("overlapping");
                                setValidationResults(res);
                                if (res.length == 0) {
                                  showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                                }
                              }}
                            >
                              Finding Overlapping
                            </Button>
                          </WrapItem>
                        )}
                    </Wrap>
                  </>
                )}

                {selectedLayers.length == 1 && matchKeyInText(selectedLayers[0].dataSource.layerName, "lanelet") && (
                  <>
                    <Text>Lanelet Methods: </Text>
                    <Wrap spacing="4px" mb={2}>
                      <WrapItem>
                        <Button
                          height={"30px"}
                          onClick={() => {
                            let res = getEntitiesWithInvalidLineTypes(viewerRef.current, selectedLayers[0].dataSource);

                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Validate Line Types
                        </Button>
                      </WrapItem>
                      <WrapItem>
                        <Button
                          height="30px"
                          onClick={() => {
                            let res = getEntitiesWithInvalidLineDistances(viewerRef.current, selectedLayers[0].dataSource);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Distance Mismatch
                        </Button>
                      </WrapItem>

                      <WrapItem>
                        <Button
                          height="30px"
                          onClick={() => {
                            let res = getLaneletsWithMissingSideIds(viewerRef.current, selectedLayers[0].dataSource);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Missing Sides
                        </Button>
                      </WrapItem>

                      <WrapItem>
                        <Button
                          height="30px"
                          onClick={() => {
                            let res = getLaneletsWithOpenSides(viewerRef.current, selectedLayers[0].dataSource);
                            setValidationResults(res);
                            if (res.length == 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Connection Sides
                        </Button>
                      </WrapItem>

                      <WrapItem>
                        <Button
                          height="30px"
                          colorPalette={"orange"}
                          onClick={() => {
                            const res = getComprehensiveLaneletIssues(viewerRef.current, selectedLayers[0].dataSource);
                            setValidationResults(res);
                            if (res.length === 0) {
                              showToast("Not Found", Type.NOTIFICATION.WARNING, { closable: true });
                            }
                          }}
                        >
                          Comprehensive review
                        </Button>
                      </WrapItem>
                    </Wrap>
                  </>
                )}

                {validationResults.length != 0 && (
                  <>
                    <Separator mb={3} />
                    <HStack>
                      <Text>Results: {validationResults.length}</Text>
                      <Button
                        height={"30px"}
                        onClick={() => {
                          setResDialog(true);
                          setValidationDialog(false);
                        }}
                      >
                        Show Table
                        <LuTable />
                      </Button>
                    </HStack>
                  </>
                )}
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      <ResultsTable dialogState={[resDialog, setResDialog]} data={validationResults} selectedDataSource={selectedLayers[0]?.dataSource} activeRule={activeRule} />
    </>
  );
}

export default ValidationTool;
